import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import api from '@utils/axios';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import I18n from '@utils/i18n';
import { datatableTimeEntriesPath } from 'routes';
import ColumnMenu from '@components/Kendo/ColumnMenu';
import ColumnMenuContext from '@components/Kendo/ColumnMenuContext';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { TimeZoneContextProvider } from '@contexts/timezone-context.tsx';
import { TimeEntriesProvider } from './TimeEntriesContext';
import { saveCacheToLocalStorage } from '../../../helpers/localStorage';
import kendoDatatableParams from '../../../helpers/kendoDatatableParams';
import MassActions from './nested/MassActions';
import useFilters from './hooks/useFilters';
import TimeEntryModal from './nested/TimeEntryModal';
import './index.css';
import { getInitialData, getInitialFilters } from './helpers/initialData';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, CACHE_KEY as DEFAULT_CACHE_KEY } from './constants';
import { actionTypes, reducer } from './reducer';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';

function Index({
  cacheKey,
  hasComputereaseTimeEntryExport,
  usersForSelect,
  ratesForSelect,
  visitTypesForSelect,
  timeZone,
  timeZonesForSelect,
  timeZonesMapping,
  hasMultiTimezone,
  trackedObjectId,
  trackedObjectType
}) {
  const initialData = useMemo(
    () => getInitialData(cacheKey, trackedObjectId, trackedObjectType, hasMultiTimezone),
    [cacheKey, trackedObjectId, trackedObjectType, hasMultiTimezone]
  );
  const [state, dispatch] = useReducer(reducer, {
    data: [],
    totalCount: 0,
    ...initialData
  });
  const [modalState, setModalState] = useState({ visible: false, timeEntryId: null });

  const { page, sort, filters, columns, data, totalCount, loading, selectAll } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  // Without this the assistant falls back to the server-rendered list_context,
  // which is built at HTML-render time — before this grid has fetched anything
  // — so it carries no filters and an unfiltered count.
  usePublishListContext({ page, sort, filters, totalCount, data });

  const selectedIds = useMemo(() => data.filter((item) => item.selected).map((item) => item.id), [data]);
  const selection = useMemo(() => ({ selectAll, selectedIds }), [selectAll, selectedIds]);

  const filtersList = useFilters({
    filters,
    dispatch,
    visitTypesForSelect,
    trackedObjectId,
    timeZonesForSelect,
    hasMultiTimezone
  });

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns, cacheKey]);

  const fetchData = useCallback(() => {
    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    api
      .get(datatableTimeEntriesPath(), {
        params: kendoDatatableParams({ page, sort, filters })
      })
      .then(({ data: timeEntriesData }) => dispatch({ type: actionTypes.DATA_LOADED, data: timeEntriesData }))
      .finally(() => dispatch({ type: actionTypes.LOADING_CHANGED, loading: false }));
  }, [page, sort, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectionChange = (event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  };

  const onHeaderSelectionChange = (event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  };

  const onSelectAll = () => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  };

  const massActionQueryParams = useMemo(() => {
    const params = kendoDatatableParams({ filters, sort });
    if (selection.selectedIds.length > 0 && !selection.selectAll) {
      params.append('checked_ids', selection.selectedIds.join(','));
    }
    return params;
  }, [filters, sort, selection]);

  const onColumnsChange = (cols) => {
    dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) });
  };

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: getInitialFilters(trackedObjectId, trackedObjectType) });
  };

  const onResetCache = () => {
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const handleModalClose = () => {
    setModalState({ visible: false, timeEntryId: null });
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchData();
  };

  const openNewModal = () => {
    setModalState({ visible: true, timeEntryId: null });
  };

  useEffect(() => {
    const newButton = document.getElementById('new-time-entry-button');
    if (!newButton) return undefined;
    newButton.addEventListener('click', openNewModal);
    return () => {
      newButton.removeEventListener('click', openNewModal);
    };
  }, []);

  const allMenuColumns = useMemo(() => {
    let availableColumns = ALL_COLUMNS;
    if (trackedObjectId) {
      availableColumns = ALL_COLUMNS.filter((column) => column !== 'trackedObjectType' && column !== 'buildingName');
    }
    if (!hasMultiTimezone) {
      availableColumns = availableColumns.filter((column) => column !== 'timeZone');
    }
    return availableColumns.map((column) => ALL_COLUMNS_DATA[column]);
  }, [trackedObjectId, hasMultiTimezone]);
  const selectedMenuColumns = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns]);
  const getScreenContext = useMemo(() => {
    if (trackedObjectType === 'Inspection' && trackedObjectId) {
      return { screen: 'inspection', screenId: trackedObjectId };
    }
    if (trackedObjectType === 'WorkOrder' && trackedObjectId) {
      return { screen: 'workOrder', screenId: trackedObjectId };
    }
    return { screen: null, screenId: null };
  }, [trackedObjectType, trackedObjectId]);

  const renderActionsCell = useCallback(
    // eslint-disable-next-line react/jsx-pascal-case, react/jsx-props-no-spreading
    (props) => <ALL_COLUMNS_DATA.actions.cell {...props} setModalState={setModalState} />,
    [setModalState]
  );
  const renderIdCell = useCallback(
    // eslint-disable-next-line react/jsx-pascal-case, react/jsx-props-no-spreading
    (props) => <ALL_COLUMNS_DATA.id.cell {...props} setModalState={setModalState} />,
    [setModalState]
  );
  const visibleColumns = useMemo(
    () =>
      ['selected', ...columns]
        .filter((column) => (column === 'timeZone' ? hasMultiTimezone : true))
        .map((column) => {
          const col = ALL_COLUMNS_DATA[column];
          if (column === 'actions') {
            return { ...col, cell: renderActionsCell };
          }
          if (column === 'id') {
            return { ...col, cell: renderIdCell };
          }
          return col;
        }),
    [columns, renderActionsCell, renderIdCell, hasMultiTimezone]
  );

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((timeEntry) => timeEntry.selected));
  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: selectedMenuColumns,
      columns: allMenuColumns,
      screen: getScreenContext.screen,
      screenId: getScreenContext.screenId
    }),
    [selectedMenuColumns, allMenuColumns, onColumnsChange, getScreenContext]
  );

  return (
    <TimeZoneContextProvider initialTimezone={timeZone} timeZonesMapping={timeZonesMapping}>
      <KendoLocalizationProvider>
        <TimeEntriesProvider screen={getScreenContext.screen} screenId={getScreenContext.screenId}>
          {modalState.visible && (
            <TimeEntryModal
              timeEntryId={modalState.timeEntryId}
              onClose={handleModalClose}
              onDataChanged={handleModalSuccess}
              usersForSelect={usersForSelect}
              ratesForSelect={ratesForSelect}
              visitTypesForSelect={visitTypesForSelect}
              timeZonesForSelect={timeZonesForSelect}
            />
          )}
          <ColumnMenuContext.Provider value={columnContextValue}>
            <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList} />
            {loading && (
              <div className="workspace__section">
                <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
                  <svg role="img">
                    <use href="/map.svg#load-spinner" />
                  </svg>
                </div>
              </div>
            )}
            {!loading && (
              <CustomScrollbar>
                <Grid
                  className="qmb-grid--01"
                  data={data}
                  total={totalCount}
                  skip={page.skip}
                  take={page.take}
                  onPageChange={(event) => {
                    dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
                  }}
                  pageable={{
                    type: 'input',
                    pageSizes: [25, 50, 100],
                    pageSizeValue: page.take
                  }}
                  pager={EnterKeyPager}
                  sortable
                  sort={sort}
                  onSortChange={(event) => {
                    dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
                  }}
                  dataItemKey="id"
                  selectedField="selected"
                  onSelectionChange={onSelectionChange}
                  onHeaderSelectionChange={onHeaderSelectionChange}>
                  <GridToolbar>
                    <MassActions
                      state={{ ...state, selectedIds }}
                      onSelectAll={onSelectAll}
                      queryParams={massActionQueryParams}
                      hasComputereaseTimeEntryExport={hasComputereaseTimeEntryExport}
                    />
                  </GridToolbar>
                  {visibleColumns.map((column) => (
                    <Column
                      key={column.id}
                      field={column.field}
                      title={column.title}
                      columnMenu={ColumnMenu}
                      cell={column.cell}
                      sortable={column.sortable}
                      width={column.width}
                      format={column.format}
                      headerSelectionValue={headerSelectionValue}
                      className={column.className}
                      headerClassName={column.headerClassName}
                    />
                  ))}
                </Grid>
              </CustomScrollbar>
            )}
          </ColumnMenuContext.Provider>
        </TimeEntriesProvider>
      </KendoLocalizationProvider>
    </TimeZoneContextProvider>
  );
}

const selectShape = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })
);

Index.propTypes = {
  cacheKey: PropTypes.string,
  hasComputereaseTimeEntryExport: PropTypes.bool,
  usersForSelect: selectShape,
  ratesForSelect: selectShape,
  visitTypesForSelect: selectShape,
  timeZone: PropTypes.string,
  timeZonesForSelect: selectShape,
  timeZonesMapping: PropTypes.object,
  trackedObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trackedObjectType: PropTypes.string,
  hasMultiTimezone: PropTypes.bool
};

Index.defaultProps = {
  cacheKey: DEFAULT_CACHE_KEY,
  hasComputereaseTimeEntryExport: false,
  usersForSelect: [],
  trackedObjectId: null,
  trackedObjectType: null,
  ratesForSelect: [],
  visitTypesForSelect: [],
  timeZonesForSelect: [],
  timeZonesMapping: {},
  timeZone: null,
  hasMultiTimezone: false
};

export default Index;
